import { useEffect, useMemo, useState } from 'react';
import DateField from '../components/DateField.jsx';
import QuickPeriod from '../components/QuickPeriod.jsx';
import Lookup from '../components/Lookup.jsx';
import ProductLookup from '../components/ProductLookup.jsx';
import GroupPicker from '../components/GroupPicker.jsx';
import DocumentPreviewModal from '../components/DocumentPreviewModal.jsx';
import { api, withQuery, money, date, monthLabel, PAYMENT_LABEL, isoDate } from '../api.js';

/**
 * Печалба от търговската дейност.
 *
 * Пълна печалба: приход минус себестойност за цялата сделка, независимо
 * какво е платено. Старата програма предлага пет начина на смятане; тук
 * е само този, защото само него ползваме.
 *
 * Приходът е без ДДС. Себестойността идва от партидата, от която е
 * излязла стоката — при липса на партида от цената по реда, а услугите
 * нямат себестойност.
 *
 * Подаръчните редове ВЛИЗАТ и това е смисълът на справката: при ремонт
 * услугата носи прихода, а вложените части носят разхода. Без тях всеки
 * ремонт изглеждаше като чиста печалба.
 */

const GROUPS = [
  ['month', 'месеци'],
  ['day', 'дни'],
  ['year', 'години'],
  ['partner', 'фирми'],
  ['maingroup', 'главни групи (Стоки / Услуги)'],
  ['group', 'групи стоки'],
  ['product', 'стоки'],
  ['warehouse', 'складове'],
  ['user', 'служители'],
  ['document', 'документи'],
  ['none', 'без групиране'],
];

const today = () => isoDate();

/*
 * Бъкетът идва от сървъра суров — за групиране по ден е "2026-08-25",
 * по месец "2026-08" (година-месец, за да се подрежда лексикографски). И
 * двата излизаха на екрана точно така, необлечени: първо годината, после
 * месеца, после деня — обратно на българския ред ден.месец.година.
 */
function bucketLabel(groupBy, bucket) {
  if (bucket == null) return 'общо';
  if (groupBy === 'day') return date(bucket);
  if (groupBy === 'month') return monthLabel(bucket);
  return bucket;
}

export default function Profit() {
  const [f, setF] = useState({
    from: today(), to: today(), groupBy: 'document', warehouseId: '', paymentMethod: '',
    groupId: '', productId: '', partnerId: '', userId: '',
  });
  const [picked, setPicked] = useState({ product: null, partner: null });
  const [warehouses, setWarehouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [operators, setOperators] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  /*
   * Подредбата по печалба е отделна от групирането — при всяко групиране
   * може да се пита „кое носи най-много" без да се сменя „Групирай по".
   * Server-side поредбата (по дата, по печалба и т.н.) си остава
   * подразбираща се; тук е само ръчно наложена подредба върху вече
   * заредените редове.
   */
  const [profitSortDir, setProfitSortDir] = useState(null);

  useEffect(() => {
    api.get('/api/warehouses').then(setWarehouses).catch(() => {});
    api.get('/api/product-groups').then(setGroups).catch(() => {});
    api.get('/api/reports/operators').then(setOperators).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(withQuery('/api/reports/profit', {
      from: f.from || undefined,
      to: f.to || undefined,
      groupBy: f.groupBy,
      warehouseId: f.warehouseId || undefined,
      paymentMethod: f.paymentMethod || undefined,
      groupId: f.groupId || undefined,
      productId: f.productId || undefined,
      partnerId: f.partnerId || undefined,
      userId: f.userId || undefined,
    }))
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [f]);

  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const t = data?.totals;

  const rows = useMemo(() => {
    const base = data?.rows ?? [];
    if (!profitSortDir) return base;
    return [...base].sort((a, b) => (Number(a.profit) - Number(b.profit)) * profitSortDir);
  }, [data, profitSortDir]);

  function sortByProfit() {
    // Първо щракване: най-голямата печалба отгоре. Повторно — обръща.
    setProfitSortDir((d) => (d === -1 ? 1 : -1));
  }

  /*
   * Първата колона по подразбиране е контрагентът — полезно е да се
   * знае чия е продажбата. Но щом е избрана точно определена стока или
   * група, всеки ред е за нея — контрагентът вече не е интересното, а
   * коя стока/група е избрана.
   */
  const groupName = groups.find((g) => String(g.id) === String(f.groupId))?.name;
  const firstCol = f.productId
    ? { label: 'Стока', value: picked.product }
    : f.groupId
      ? { label: 'Група', value: groupName }
      : { label: 'Контрагент', value: null };

  /*
   * Търсене по конкретна стока/група/фирма не пита „кой документ" — пита
   * „на кого, кога и по коя фактура". Затова таблицата тук е друга: дата
   * и фактура вместо номера на документа, а количеството се сборува
   * отдолу. Клиентска колона се показва отделно само когато не е вече
   * самата първа колона (при филтър по фирма контрагентът е една и съща
   * стойност за всеки ред — вече е firstCol, втора колона би била излишна).
   */
  const detailView = f.groupBy === 'document' && (f.productId || f.groupId || f.partnerId);
  const showClientCol = detailView && firstCol.label !== 'Контрагент';
  const totalQuantity = data?.rows?.reduce((s, r) => s + Number(r.quantity), 0) ?? 0;

  return (
    <>
      <div className="page-head">
        <h1>Печалба</h1>
        <div className="spacer" />
        {t && (
          <>
            <span className="badge">{t.documents} документа</span>
            <span className="badge">приход {money(t.revenue)}</span>
            <span className="badge">разход {money(t.cost)}</span>
            <span className={`badge ${t.profit >= 0 ? 'ok' : 'danger'}`}>
              печалба {money(t.profit)}{t.margin !== null ? ` · ${t.margin}%` : ''}
            </span>
          </>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="toolbar">
        <label className="field">
          От дата
          <DateField value={f.from} onChange={(v) => set({ from: v })} />
        </label>
        <label className="field">
          До дата
          <DateField value={f.to} onChange={(v) => set({ to: v })} />
        </label>
        <div className="field">
          <span aria-hidden="true" style={{ visibility: 'hidden' }}>·</span>
          <QuickPeriod onPick={({ from, to }) => set({ from, to })} />
        </div>
        <label className="field">
          Групирай по
          <select value={f.groupBy} onChange={(e) => set({ groupBy: e.target.value })}>
            {GROUPS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <label className="field">
          Склад
          <select value={f.warehouseId} onChange={(e) => set({ warehouseId: e.target.value })}>
            <option value="">всички</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label className="field">
          Плащане
          <select value={f.paymentMethod} onChange={(e) => set({ paymentMethod: e.target.value })}>
            <option value="">всички</option>
            {Object.entries(PAYMENT_LABEL).map(([k, label]) =>
              <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <label className="field">
          Група
          <GroupPicker
            label="всички"
            value={f.groupId}
            valueLabel={groupName}
            onPick={(id) => set({ groupId: id })}
            onClear={() => set({ groupId: '' })}
          />
        </label>
        <label className="field">
          Служител
          <select value={f.userId} onChange={(e) => set({ userId: e.target.value })}>
            <option value="">всички</option>
            {operators.map((o) => (
              <option key={o.id} value={o.id}>{o.full_name}</option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 280 }}>
          Стока
          <ProductLookup
            value={f.productId}
            label={picked.product}
            placeholder="име или код"
            style={{ width: '100%' }}
            onPick={(id, name) => {
              setPicked((p) => ({ ...p, product: name }));
              set({ productId: id ?? '' });
            }}
          />
        </label>
        <label className="field">
          Фирма
          <Lookup
            url="/api/partners"
            value={f.partnerId}
            label={picked.partner}
            placeholder="име на фирма"
            onPick={(id, name) => {
              setPicked((p) => ({ ...p, partner: name }));
              set({ partnerId: id ?? '' });
            }}
          />
        </label>
      </div>

      <div className="note">
        Приходът е без ДДС. Себестойността е цената на партидата, от която е излязла стоката.
        Вложеното в ремонт влиза с разход и нулев приход — затова редовете „подарък“ намаляват
        печалбата, а услугата до тях я носи. Анулираните редове не се броят, връщанията се
        приспадат.
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="compact">
            <thead>
              {detailView ? (
                <tr>
                  {showClientCol && <th>Клиент</th>}
                  <th>{firstCol.label}</th>
                  <th>Дата</th>
                  <th>Фактура</th>
                  <th className="num">Документи</th>
                  <th className="num">Количество</th>
                  <th className="num">Приход</th>
                  <th className="num">Разход</th>
                  <th className={`num sortable ${profitSortDir ? 'on' : ''}`} onClick={sortByProfit} title="подреди по печалба">
                    Печалба
                    <span className="arrow">{profitSortDir ? (profitSortDir < 0 ? '▼' : '▲') : '▼'}</span>
                  </th>
                  <th className="num" title="Печалбата като дял от оборота без ДДС">% от оборота</th>
                </tr>
              ) : (
                <tr>
                  <th>{firstCol.label}</th>
                  <th>{data?.label ?? ''}</th>
                  <th className="num">Документи</th>
                  <th className="num">Количество</th>
                  <th className="num">Приход</th>
                  <th className="num">Разход</th>
                  <th className={`num sortable ${profitSortDir ? 'on' : ''}`} onClick={sortByProfit} title="подреди по печалба">
                    Печалба
                    <span className="arrow">{profitSortDir ? (profitSortDir < 0 ? '▼' : '▲') : '▼'}</span>
                  </th>
                  <th className="num" title="Печалбата като дял от оборота без ДДС">% от оборота</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={detailView ? (showClientCol ? 10 : 9) : 8} className="empty">Зареждане…</td></tr>
              )}
              {!loading && !rows.length && (
                <tr><td colSpan={detailView ? (showClientCol ? 10 : 9) : 8} className="empty">
                  Няма продажби за този период.
                </td></tr>
              )}
              {!loading && detailView && rows.map((r, i) => (
                <tr key={r.bucket ?? i}>
                  {showClientCol && (
                    <td><strong>
                      <button className="link" onClick={() => setPreviewId(r.document_id)}>
                        {r.partner_name ?? '—'}
                      </button>
                    </strong></td>
                  )}
                  <td>
                    <strong>
                      {showClientCol ? (firstCol.value ?? '—') : (
                        <button className="link" onClick={() => setPreviewId(r.document_id)}>
                          {firstCol.value ?? (r.partner_name ?? '—')}
                        </button>
                      )}
                    </strong>
                  </td>
                  <td>{date(r.doc_date)}</td>
                  <td className="muted">
                    {r.invoice_no
                      ? <button className="link" onClick={() => setPreviewId(r.document_id)}>{r.invoice_no}</button>
                      : '—'}
                  </td>
                  <td className="num">{r.documents}</td>
                  <td className="num">{Number(r.quantity).toLocaleString('bg-BG')}</td>
                  <td className="num">{money(r.revenue)}</td>
                  <td className="num">{money(r.cost)}</td>
                  <td className="num">
                    <b className={Number(r.profit) < 0 ? 'negative' : undefined}>
                      {money(r.profit)}
                    </b>
                  </td>
                  <td className="num">{r.margin === null ? '—' : `${r.margin}%`}</td>
                </tr>
              ))}
              {!loading && !detailView && rows.map((r, i) => (
                <tr key={r.bucket ?? i}>
                  {/* `r.partner_name` е случайна фирма от бъкета (виж бележката
                      в reports.js) — значеща е само когато групирането Е по
                      контрагент; при ден/месец/склад/... бъкетът вече си има
                      собствен, точен етикет в следващата колона. */}
                  <td><strong>
                    {/* По документ — клиентът на документа (08.10.2026). */}
                    {firstCol.value ?? (['partner', 'document'].includes(f.groupBy)
                      ? (r.partner_name ?? 'на каса') : '—')}
                  </strong></td>
                  <td>
                    {bucketLabel(f.groupBy, r.bucket)}
                    {f.groupBy === 'document' && Number(r.stornirano) > 0 && (
                      <span className="badge muted" style={{ marginLeft: 6 }}>сторнирана</span>
                    )}
                  </td>
                  <td className="num">{r.documents}</td>
                  <td className="num">{Number(r.quantity).toLocaleString('bg-BG')}</td>
                  <td className="num">{money(r.revenue)}</td>
                  <td className="num">{money(r.cost)}</td>
                  <td className="num">
                    <b className={Number(r.profit) < 0 ? 'negative' : undefined}>
                      {money(r.profit)}
                    </b>
                  </td>
                  <td className="num">{r.margin === null ? '—' : `${r.margin}%`}</td>
                </tr>
              ))}
            </tbody>
            {t && !loading && detailView && (
              <tfoot>
                <tr>
                  {showClientCol && <th />}
                  <th />
                  <th />
                  <th>Общо</th>
                  <th className="num">{t.documents}</th>
                  <th className="num">{totalQuantity.toLocaleString('bg-BG')}</th>
                  <th className="num">{money(t.revenue)}</th>
                  <th className="num">{money(t.cost)}</th>
                  <th className="num">{money(t.profit)}</th>
                  <th className="num">{t.margin === null ? '—' : `${t.margin}%`}</th>
                </tr>
              </tfoot>
            )}
            {t && !loading && !detailView && (
              <tfoot>
                <tr>
                  <th />
                  <th>Общо</th>
                  <th className="num">{t.documents}</th>
                  <th className="num" />
                  <th className="num">{money(t.revenue)}</th>
                  <th className="num">{money(t.cost)}</th>
                  <th className="num">{money(t.profit)}</th>
                  <th className="num">{t.margin === null ? '—' : `${t.margin}%`}</th>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {previewId && (
        <DocumentPreviewModal documentId={previewId} onClose={() => setPreviewId(null)} />
      )}
    </>
  );
}
